import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Bed, Save, Bath, PoundSterling, Search, Settings2, Copy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BeddingRatesEditor from "@/components/BeddingRatesEditor";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Session Z69 — Bedding supply cost matrix.
// Lists every property with its bedding config + per-turnover and
// projected monthly linen/towel hire cost. Operators edit bed counts
// inline. Rates editable at the top (admin-only control surface).

function _fmtGBP(n) {
  return `£${Number(n || 0).toFixed(2)}`;
}

export default function BeddingMatrix() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);
  const [portfolioMonthly, setPortfolioMonthly] = useState(0);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [copyTarget, setCopyTarget] = useState(null);
  const [copyQuery, setCopyQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bedding/matrix");
      setRows(data?.items || []);
      setPortfolioMonthly(Number(data?.portfolio_monthly_total || 0));
      setEdits({});
    } catch (err) {
      toast.error(apiError(err, "Load failed"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => (r.property_name || "").toLowerCase().includes(needle));
  }, [rows, q]);

  const updateEdit = (pid, key, value) => {
    setEdits((prev) => ({
      ...prev,
      [pid]: { ...(prev[pid] || {}), [key]: value },
    }));
  };

  const buildPayload = (r) => {
    const edit = edits[r.property_id] || {};
    const base = r.bedding || {};
    return {
      double_beds: Number(edit.double_beds ?? base.double_beds ?? 0),
      king_beds: Number(edit.king_beds ?? base.king_beds ?? 0),
      super_king_beds: Number(edit.super_king_beds ?? base.super_king_beds ?? 0),
      single_beds: Number(edit.single_beds ?? base.single_beds ?? 0),
      full_bathrooms: Number(edit.full_bathrooms ?? base.full_bathrooms ?? 0),
      separate_wcs: Number(edit.separate_wcs ?? base.separate_wcs ?? 0),
      pillowcases_per_double: Number(edit.pillowcases_per_double ?? base.pillowcases_per_double ?? 2),
      pillowcases_per_single: Number(edit.pillowcases_per_single ?? base.pillowcases_per_single ?? 1),
    };
  };

  const save = async (r) => {
    setSaving(r.property_id);
    try {
      await api.put(`/properties/${r.property_id}/bedding`, buildPayload(r));
      toast.success(`${r.property_name} saved`);
      load();
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally {
      setSaving(null);
    }
  };

  // Task — Bedding Preset Library. Pulls every bedding field from the
  // chosen source into the target row's edit buffer so operators can
  // review before saving. Deliberately does NOT auto-save — a human
  // eyeball on the beds-vs-bathrooms line always catches mis-copies.
  const copyFrom = (sourceRow) => {
    if (!copyTarget) return;
    const base = sourceRow.bedding || {};
    const next = {
      double_beds: Number(base.double_beds ?? 0),
      king_beds: Number(base.king_beds ?? 0),
      super_king_beds: Number(base.super_king_beds ?? 0),
      single_beds: Number(base.single_beds ?? 0),
      full_bathrooms: Number(base.full_bathrooms ?? 0),
      separate_wcs: Number(base.separate_wcs ?? 0),
      pillowcases_per_double: Number(base.pillowcases_per_double ?? 2),
      pillowcases_per_single: Number(base.pillowcases_per_single ?? 1),
    };
    setEdits((prev) => ({ ...prev, [copyTarget.property_id]: next }));
    toast.success(`Copied from ${sourceRow.property_name} — review & save`);
    setCopyTarget(null);
    setCopyQuery("");
  };

  const copyCandidates = useMemo(() => {
    if (!copyTarget) return [];
    const needle = copyQuery.trim().toLowerCase();
    return rows
      .filter((r) => r.configured && r.property_id !== copyTarget.property_id)
      .filter((r) => !needle || (r.property_name || "").toLowerCase().includes(needle))
      .sort((a, b) => (a.property_name || "").localeCompare(b.property_name || ""));
  }, [rows, copyTarget, copyQuery]);

  const Cell = ({ r, k, w = "w-14" }) => {
    const base = (r.bedding || {})[k] ?? 0;
    const val = edits[r.property_id]?.[k] ?? base;
    return (
      <input
        data-testid={`bedding-${k}-${r.property_id}`}
        type="number"
        min="0"
        value={val}
        onChange={(e) => updateEdit(r.property_id, k, e.target.value)}
        className={`${w} h-9 text-center rounded-md border border-slate-200 bg-white px-1 text-sm tabular-nums`}
      />
    );
  };

  return (
    <section className="ns-card p-6" data-testid="bedding-matrix">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="min-w-0">
          <div className="ns-label">Bedding supply</div>
          <div className="font-serif text-xl mt-1">
            Linen &amp; towel hire · {_fmtGBP(portfolioMonthly)} this month
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Johnsons weekly minimum rates, inc VAT. Feeds the monthly financial report alongside cleaning costs.
          </div>
        </div>
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
          <Bed className="w-5 h-5" />
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="bedding-matrix-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search property"
            className="pl-9 h-10 bg-white border border-slate-200 rounded-md px-3 text-sm w-full"
          />
        </div>
        <div className="text-xs text-slate-500" data-testid="bedding-matrix-stats">
          {filtered.length} propert{filtered.length === 1 ? "y" : "ies"} · {rows.filter((r) => r.configured).length} configured
        </div>
        {isOwner && (
          <button
            type="button"
            data-testid="bedding-rates-open"
            onClick={() => setRatesOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white hover:border-teal-600 hover:text-teal-700 h-9 px-3 text-xs"
          >
            <Settings2 className="w-3 h-3" />
            Edit rate card
          </button>
        )}
      </div>
      <BeddingRatesEditor open={ratesOpen} onOpenChange={setRatesOpen} onSaved={load} />

      <Dialog open={!!copyTarget} onOpenChange={(v) => { if (!v) { setCopyTarget(null); setCopyQuery(""); } }}>
        <DialogContent className="max-w-lg" data-testid="bedding-copy-picker">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <Copy className="w-5 h-5 text-teal-700" />
              Copy bedding to {copyTarget?.property_name}
            </DialogTitle>
            <DialogDescription>
              Pick a configured property. Its beds, bathrooms, and pillowcase ratios drop into the row so you can review before saving.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="bedding-copy-search"
                autoFocus
                value={copyQuery}
                onChange={(e) => setCopyQuery(e.target.value)}
                placeholder="Search configured properties"
                className="pl-9 h-10 bg-white border border-slate-200 rounded-md px-3 text-sm w-full"
              />
            </div>
            <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-100 bg-white" data-testid="bedding-copy-list">
              {copyCandidates.length === 0 && (
                <div className="text-xs text-slate-400 italic text-center py-6">
                  No configured properties match.
                </div>
              )}
              {copyCandidates.map((src) => {
                const b = src.bedding || {};
                const summary = [
                  `${b.double_beds || 0} dbl`,
                  `${b.king_beds || 0} king`,
                  `${b.super_king_beds || 0} SK`,
                  `${b.single_beds || 0} sgl`,
                  `${b.full_bathrooms || 0} bath`,
                ].join(" · ");
                return (
                  <button
                    key={src.property_id}
                    type="button"
                    data-testid={`bedding-copy-pick-${src.property_id}`}
                    onClick={() => copyFrom(src)}
                    className="w-full flex items-start justify-between gap-3 px-3 py-2 border-b border-slate-100 last:border-0 text-left hover:bg-stone-50"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{src.property_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{summary}</div>
                    </div>
                    <div className="text-[11px] tabular-nums text-slate-500 shrink-0 mt-0.5">
                      {_fmtGBP(src.cost?.total_per_turnover)}/turn
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="overflow-x-auto -mx-6">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-slate-500">
            <tr>
              <th className="ns-label px-6 py-3 text-left">Property</th>
              <th className="ns-label px-3 py-3 text-center" title="Double beds">Dbl</th>
              <th className="ns-label px-3 py-3 text-center" title="King beds">King</th>
              <th className="ns-label px-3 py-3 text-center" title="Super king">SK</th>
              <th className="ns-label px-3 py-3 text-center" title="Single beds">Sgl</th>
              <th className="ns-label px-3 py-3 text-center" title="Full bathrooms"><Bath className="w-3 h-3 inline" /></th>
              <th className="ns-label px-3 py-3 text-center" title="Separate WCs">WC</th>
              <th className="ns-label px-6 py-3 text-right">Per turnover</th>
              <th className="ns-label px-3 py-3 text-right">T/m</th>
              <th className="ns-label px-6 py-3 text-right">This month</th>
              <th className="w-20 px-6 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="text-center py-10 text-slate-400 italic">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={11} className="text-center py-10 text-slate-400 italic">No properties.</td></tr>
            )}
            {filtered.map((r) => {
              const dirty = !!edits[r.property_id];
              const autoSeeded = r.bedding?.seeded_from === "portfolio_median";
              return (
                <tr key={r.property_id} data-testid={`bedding-row-${r.property_id}`} className="border-t border-slate-100 hover:bg-stone-50/40">
                  <td className="px-6 py-3 font-medium text-slate-800 min-w-[200px]">
                    {r.property_name}
                    {!r.configured && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">
                        unset
                      </span>
                    )}
                    {autoSeeded && !dirty && (
                      <span
                        data-testid={`bedding-starter-chip-${r.property_id}`}
                        title="This row was seeded with the portfolio-median bed layout when the property was created. Review the counts and tap Save to confirm."
                        className="ml-2 text-[10px] uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5"
                      >
                        auto · review
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center"><Cell r={r} k="double_beds" /></td>
                  <td className="px-2 py-2 text-center"><Cell r={r} k="king_beds" /></td>
                  <td className="px-2 py-2 text-center"><Cell r={r} k="super_king_beds" /></td>
                  <td className="px-2 py-2 text-center"><Cell r={r} k="single_beds" /></td>
                  <td className="px-2 py-2 text-center"><Cell r={r} k="full_bathrooms" /></td>
                  <td className="px-2 py-2 text-center"><Cell r={r} k="separate_wcs" /></td>
                  <td className="px-6 py-3 text-right tabular-nums text-slate-700" title={`Duvet+Sheet ${_fmtGBP(r.cost.duvet_sheet)} · Pillowcase ${_fmtGBP(r.cost.pillowcase)} · Bedroom ${_fmtGBP(r.cost.bedroom_towel)} · Bathroom ${_fmtGBP(r.cost.bathroom)}`}>
                    {_fmtGBP(r.cost.total_per_turnover)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-slate-500">
                    {r.turnovers_this_month}
                  </td>
                  <td className="px-6 py-3 text-right tabular-nums font-semibold text-slate-900">
                    {_fmtGBP(r.monthly_cost)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        data-testid={`bedding-copy-${r.property_id}`}
                        onClick={() => { setCopyTarget(r); setCopyQuery(""); }}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white hover:border-teal-600 hover:text-teal-700 text-[11px] px-2 py-1"
                        title="Copy bedding config from another property"
                      >
                        <Copy className="w-3 h-3" />
                        Copy from
                      </button>
                      {dirty && (
                        <button
                          type="button"
                          data-testid={`bedding-save-${r.property_id}`}
                          onClick={() => save(r)}
                          disabled={saving === r.property_id}
                          className="inline-flex items-center gap-1 rounded-full bg-teal-600 hover:bg-teal-700 text-white text-[11px] px-3 py-1"
                        >
                          <Save className="w-3 h-3" />
                          {saving === r.property_id ? "…" : "Save"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-stone-50 text-slate-700 font-semibold">
              <tr>
                <td colSpan={9} className="px-6 py-3 text-right">
                  <PoundSterling className="w-3 h-3 inline mr-1" />
                  Portfolio linen cost this month
                </td>
                <td className="px-6 py-3 text-right tabular-nums">
                  {_fmtGBP(portfolioMonthly)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}
